import * as React from 'react';
import styled from 'styled-components';
import Text12 from '@sdk/components/ui/typography/Text12';
import dynamicLaborValue from '@sdk/labor/helpers/dynamicLaborValue';
import displayDuration from '@sdk/utils/displayDuration';
import useAuth from '@sdk/authentication/useAuth';
import { useCurrency } from '@sdk/common/currency/CurrencyContext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import EllipsisStyle from '@sdk/components/ui/EllipsisStyle';
import type { CurrencyType } from '@sdk/common/currency/types/CurrencyType';
import type { LaborUISchemaType } from '@sdk/labor/types';

import type { RowDataType } from '../types/RowType';
import type { FormattedDateString } from '../types/FormattedDateString';
import type { TotalsWithLabor } from '../types/GroupsArrayType';
import type { OptionsFilterType } from '../types';
import FlexRow from '../ui/FlexRow';
import Tooltip from '../../components/Tooltip';

import ScheduleCell, { ScheduleCellProps } from './ScheduleCell';

const BORDER_COLOR = 'rgba(0, 0, 0, 0.08)';

const Row = styled(FlexRow)`
  height: 100%;
  background-color: #f3f5f7;
`;

type CellProps = {
  paddingLeft?: number;
  paddingRight?: number;
};
const Cell = styled(ScheduleCell)<CellProps & ScheduleCellProps>`
  ${EllipsisStyle}
  flex-direction: column;
  padding-top: 8px;
  padding-bottom: 8px;
  padding-right: ${({ paddingRight }): number => paddingRight || 0}px;
  padding-left: ${({ paddingLeft }): number => paddingLeft || 0}px;
  border-left: ${({ leftBorder }): number => (leftBorder ? 1 : 0)}px solid ${BORDER_COLOR};
  border-bottom: 1px solid ${BORDER_COLOR};
  border-top: 1px solid ${BORDER_COLOR};
`;

type SummaryTotalsType = { [key in string]: number };
type DisplayLaborProps = {
  optionsFilter: OptionsFilterType;
  laborUISchema: LaborUISchemaType;
  totals: TotalsWithLabor;
  currency: CurrencyType;
};
const DisplayLabor: React.FC<React.PropsWithChildren<DisplayLaborProps>> = ({
  optionsFilter,
  laborUISchema,
  totals = {},
  currency,
}) => {
  const { dayTotals } = laborUISchema.schema.calendarUISchema;
  const items = dayTotals.items.filter((item) => laborUISchema.evalIf(item, optionsFilter));

  return (
    <>
      {items.map((item, index) => {
        const { unit, property } = item;
        const value = totals[property] || 0;
        const { red, green } = laborUISchema.evalColor(
          item,
          totals as SummaryTotalsType,
          optionsFilter
        );
        const themeColor = red
          ? ThemeColorType.danger
          : green
            ? ThemeColorType.success
            : ThemeColorType.iconGray;

        return (
          <Text12 key={index} themeColor={themeColor}>
            {dynamicLaborValue({ value, unit, currency })}
          </Text12>
        );
      })}
    </>
  );
};

type SummaryData = {
  hours: string;
  people: string;
};

type Props = {
  columns: Array<FormattedDateString>;
  rowData: RowDataType;
  laborUISchema: LaborUISchemaType;
  optionsFilter: OptionsFilterType;
};
const SummaryRow: React.FC<React.PropsWithChildren<Props>> = ({
  columns,
  rowData,
  laborUISchema,
  optionsFilter,
}) => {
  const { totals, totalsPerDay, hasNameColumn = true } = rowData;
  const { currentUser } = useAuth();
  const { currency } = useCurrency();
  const { hasAccessToLaborCost } = currentUser;

  return (
    <Row>
      {hasNameColumn && (
        <Cell paddingRight={16}>
          {optionsFilter.showCountOfHours && (
            <>
              <Text12 uppercase bold themeColor={ThemeColorType.gray}>
                Scheduled hours
              </Text12>
              <Text12 uppercase bold themeColor={ThemeColorType.gray}>
                Employees
              </Text12>
            </>
          )}
          {hasAccessToLaborCost && (
            <>
              {optionsFilter.showLaborCost && (
                <Text12 uppercase bold themeColor={ThemeColorType.gray}>
                  Labor cost
                </Text12>
              )}
              {optionsFilter.showLaborPercentage && (
                <Text12 uppercase bold themeColor={ThemeColorType.gray}>
                  Labor %
                </Text12>
              )}
            </>
          )}
        </Cell>
      )}
      {columns.map((day) => {
        const { hours, people } = getSummary(totals ? totals[day] : totalsPerDay[day]);

        return (
          <Cell key={day} paddingLeft={8} paddingRight={8}>
            {optionsFilter.showCountOfHours && (
              <>
                <Text12 themeColor={ThemeColorType.iconGray}>{hours}</Text12>
                <Text12 themeColor={ThemeColorType.iconGray}>{people}</Text12>
              </>
            )}
            {hasAccessToLaborCost && (
              <DisplayLabor
                optionsFilter={optionsFilter}
                laborUISchema={laborUISchema}
                totals={totals ? totals[day] : totalsPerDay[day]}
                currency={currency}
              />
            )}
            {!hasAccessToLaborCost && optionsFilter.showLaborCost && (
              <>
                <Text12
                  themeColor={ThemeColorType.separatorOnGray}
                  data-tip="Tracking of labor costs is available in premium"
                  data-place="top">
                  {dynamicLaborValue({ value: 0, unit: 'currency', currency })}
                </Text12>
                <Tooltip attachToBody />
              </>
            )}
          </Cell>
        );
      })}
    </Row>
  );
};

function getSummary(totals: TotalsWithLabor): SummaryData {
  if (!totals) {
    return {
      hours: displayDuration(0),
      people: '0 people',
    };
  }
  const { paidMinutes = 0, users = {} } = totals;
  const employeeCount = Object.keys(users)
    .map((id) => users[id])
    .filter(Boolean).length;

  return {
    hours: displayDuration(paidMinutes),
    people: `${employeeCount} people`,
  };
}

export default SummaryRow;
